'use client';

import clsx from 'clsx';
import { useParams } from 'next/navigation';
import { useBetslipStore } from '@/store/betslip';
import { useSiteStore } from '@/store/site';
import { BasicBetSlipHeaderGroup, BetslipModalHeader } from '../betslip-header';
import { BetslipContainer } from '../betslip-container';
import { AddTicketEventInterface, BetAction, BetParlayTicketsModel, BetSourceType, BetTicketModel, ChildTabType, RemoveTicketEventInterface, TicketModel, useTicketBet, useTicketInfo } from '@/hooks/query';
import { useEffect, useRef } from 'react';
import { useOuterClick } from '@nf/utils-common/hooks';
import { useQueryClient } from '@tanstack/react-query';
import queryKeys from '@/constants/query-keys';
import { isDesktop } from 'react-device-detect';
import { useBetslipContext } from '../contexts/betslip-context';
import { betslipStyle } from '../betslip.style';
import { useAppearanceStore } from '@/store/appearance';
import { smpService } from '@/features/single-match-parlay/services/smp.service';
import { BookedBetPromoteHint } from '@/features/betslip/betslip-booking/components/promote-hint';
import { useFloatBetslip } from '../hook/use-float-betslip';
import { useAsideBetslip } from '../hook/use-aside-betslip';
import { FloatQuickBet } from '../betslip-container/components/float-quick-bet';
import { QuickBetSettingModal } from '@/features/quick-bet-setting-modal';
import { AsideEmptyPage } from '../betslip-container/components/empty-page/aside-empty-page';
import { KafkaNote } from '@nf/utils-common/constants';
import { setKafkaWithStringAction } from '@/hooks/query/use-kafka-log';

export const Betslip = () => {
	const [
		betslipType,
		isQuickBet,
		isQuickBetSettingModalOpen,
		isExpand,
		isQuickbetExpand,
		isBookingBet,
		currentMultiTickets,
		useVoucher,
		betAction,
		sourceStorage,
		betTimer,
		setIsConfirmBet,
		syncTicket,
		syncBetTicket,
		setFocusOddsKey,
		setSelectAllOddsKey,
		toggleIsExpand,
		setBetAction,
		onOddsButtonClick,
		removeTicket,
		useSabaCoin,
		setBetslipTab,
		setBetTimer,
		setIsQuickBetSettingModalOpen
	] = useBetslipStore(state => [
		state.betslipType,
		state.isQuickBet,
		state.isQuickBetSettingModalOpen,
		state.isExpand,
		state.isQuickbetExpand,
		state.isBookingBet,
		state.currentMultiTickets,
		state.useVoucher,
		state.betAction,
		state.sourceStorage,
		state.betTimer,
		state.setIsConfirmBet,
		state.syncTicket,
		state.syncBetTicket,
		state.setFocusOddsKey,
		state.setSelectAllOddsKey,
		state.toggleIsExpand,
		state.setBetAction,
		state.onOddsButtonClick,
		state.removeTicket,
		state.useSabaCoin,
		state.setBetslipTab,
		state.setBetTimer,
		state.setIsQuickBetSettingModalOpen
	]);
	const view = useAppearanceStore(state => state.view);
	const [isParlayMode] = useSiteStore(state => [state.isParlayMode]);
	const { oddsType, isIndependentMode } = useBetslipContext();
	const { activeFloatBetSlip } = useFloatBetslip();
	const { activeAsideBetSlip } = useAsideBetslip();
	const selectRef = useRef<HTMLDivElement>(null);
	useOuterClick(selectRef, () => {
		setFocusOddsKey('');
		setSelectAllOddsKey('');
	});

	const queryClient = useQueryClient();


	const { syncTicketResponse, refetch } = useTicketInfo(currentMultiTickets);
	const { betTicketResponse, isFetching } = useTicketBet();
	const ticketsRef = useRef<TicketModel[]>();
	const hasSmp = currentMultiTickets.some(smpService.isSmpTicket);
	const ticketKeys = currentMultiTickets.map(ticket => ticket.key).join(',');

	// hide app bar on single match page
	const params = useParams();
	const isSingleMatchPage = params.matchId !== undefined;

	useEffect(() => {
		document.addEventListener('add-ticket-event', addTicketFromEvent, false);
		document.addEventListener('remove-ticket-event', removeTicketFromEvent, false);
		document.addEventListener('get-odds-keys', sendCurrentOddsKeysByEvent, false);

		return () => {
			document.removeEventListener('add-ticket-event', addTicketFromEvent, false);
			document.removeEventListener('remove-ticket-event', removeTicketFromEvent, false);
			document.removeEventListener('get-odds-keys', sendCurrentOddsKeysByEvent, false);
		};
	}, []);

	useEffect(() => {
		ticketsRef.current = currentMultiTickets;
	}, [currentMultiTickets]);

	const sendCurrentOddsKeysByEvent = () => {
		const detail = ticketsRef.current?.map(ticket => ticket.key) ?? [];
		document.dispatchEvent(new CustomEvent('update-ticket-keys', { detail }));
	}

	const addTicketFromEvent = (event: CustomEventInit<AddTicketEventInterface>) => {
		if (event.detail) {
			const { oddsKey, ticketModel } = event.detail;
			onOddsButtonClick(ticketModel, oddsKey, 'null', undefined, isParlayMode);
		}
	}

	const removeTicketFromEvent = (event: CustomEventInit<RemoveTicketEventInterface>) => {
		if (event.detail) {
			const { oddsKey } = event.detail;
			removeTicket(oddsKey, 'null');
		}
	}

	const setPromotionTabKafka = (multiTickets?: BetTicketModel[], parlayData?: BetParlayTicketsModel) => {
		const promotionSourceStorage = sourceStorage.get('promotion') ?? new Map<ChildTabType, Array<string>>();
		let hasData = false;
		if (promotionSourceStorage?.size > 0) {
			promotionSourceStorage.forEach((values) => {
				if (values.length > 0) {
					hasData = true;
				}
			});
		}

		if (!hasData) {
			return;
		}

		const valueToKeyMap = new Map();
		const tabTypeToInt: Record<Exclude<ChildTabType, undefined>, number> = {
			'WinningRate': 1,
			'Turnover': 2,
			'Payout': 3,
			'HotMatch': 4,
			'HotBetting': 5,
			'null': 99,
		};

		promotionSourceStorage.forEach((values, key) => {
			values.forEach(value => {
				valueToKeyMap.set(value, key);
			});
		});

		if (multiTickets) {
			const betListItems = multiTickets
				.filter(ticket => [0, 1].includes(ticket.ErrorCode))
				.map(ticket => {
					const key = valueToKeyMap.get(ticket.Key);
					if (key) {
						return { ...ticket, key };
					}
					return null;
				})
				.filter(item => item !== null)
				.map(item => `${tabTypeToInt[item?.key as ChildTabType]}-${item?.TransId_Cash}`)
				.join(',');

			if (betListItems !== '') {
				const kafkaData = {
					note: KafkaNote.PromotionBet,
					type: multiTickets.length > 1 ? 2 : 1,
					action: betListItems,
					topic: 0
				};
				setKafkaWithStringAction(kafkaData);
			}
		}

		if (parlayData && [0, 1].includes(parlayData.Common.ErrorCode)) {
			const betListItems =
				Array.from(valueToKeyMap.values())
					.map(item => tabTypeToInt[item as ChildTabType])
					.join(',');

			const kafkaData = {
				note: KafkaNote.PromotionBet,
				type: 3,
				action: `${parlayData.TransId_Combo}-${betListItems}`,
				topic: 0
			};
			setKafkaWithStringAction(kafkaData);
		}
	}

	useEffect(() => {
		sendCurrentOddsKeysByEvent();
		if (currentMultiTickets.length && !hasSmp) {
			refetch();
		} else if (activeFloatBetSlip && isExpand) { // 無票時自動關閉 parlay modal
			toggleIsExpand();
		} else {
			setBetAction(BetAction.Default);
		}

		if (isIndependentMode && !currentMultiTickets.length) {
			setBetslipTab(isParlayMode ? 'parlay' : 'single');
		}
		const storage = sourceStorage.get('promotion') ?? new Map<ChildTabType, Array<string>>();
		if (storage?.size > 0) {
			let hasData = false;
			storage.forEach((values) => {
				if (values.length > 0) {
					hasData = true;
				}
			});
			if (hasData) {
				setBetAction(BetAction.MasterRank);
			}
		} else {
			setBetAction(BetAction.Default);
		}
	}, [currentMultiTickets.length, ticketKeys, useVoucher, hasSmp, useSabaCoin]);

	useEffect(() => {
		if (syncTicketResponse) {
			syncTicket(syncTicketResponse.MultiData, syncTicketResponse.ParlayData, oddsType, view, syncTicketResponse.OddsPromotionCode);
		}
	}, [syncTicketResponse]);

	useEffect(() => {
		if (isFetching) {
			clearTimeout(betTimer);

			const timer = setTimeout(() => {
				if (isFetching) {
					document.dispatchEvent(new CustomEvent('show-toast-msg', {
						detail: { resourceKey: 'lbl_BetProcessMsg' }
					}));
				}
			}, 1000); // 若超過 1 秒未收到 response 則顯示提示

			setBetTimer(timer as unknown as number);

		} else if (betTicketResponse && isFetching === false) {
			if (betTimer) {
				document.dispatchEvent(new CustomEvent('clear-toast-msg', {
					detail: { resourceKey: 'lbl_BetProcessMsg' }
				}));
				clearTimeout(betTimer);
			}

			let isAccepted = false;

			const multiTickets = betTicketResponse.MultiData?.ItemList;
			const parlayData = betTicketResponse.ParlayData;

			if (multiTickets) {
				isAccepted = multiTickets.some(ticket => [0, 1].includes(ticket.ErrorCode));
			}

			if (!isAccepted && parlayData) {
				isAccepted = [0, 1].includes(parlayData.Common.ErrorCode);
			}

			// 確認下注有沒有收票
			if (isAccepted && !isQuickBet) {
				setIsConfirmBet(true);
				if (multiTickets || parlayData) {
					setPromotionTabKafka(multiTickets, parlayData);
				}
			}

			syncBetTicket(multiTickets, parlayData, oddsType, view);

			// refetch my bets
			if (isDesktop) {
				queryClient.invalidateQueries({
					queryKey: [queryKeys.UNSETTLED_CATEGORY_INFO]
				});
			}
			queryClient.invalidateQueries({
				queryKey: [queryKeys.UNSETTLED_STATUS_COUNT_INFO]
			});
		}
	}, [isFetching]);

	useEffect(() => {
		// 切到 float mode 若 betslip 開著需自動收合, 從 bet again 進入則需維持展開
		if (activeFloatBetSlip && isExpand && betAction !== BetAction.BetAgain) {
			toggleIsExpand();
		}
	}, [activeFloatBetSlip]);

	const getIsBetslipExpand = () => {
		// comment out because of bet slip v2 parlay adjustment
		// if (view === ViewEnum.mars && !activeFloatBetSlip) {
		// 	const ticketCount = currentMultiTickets.length;
		// 	if (ticketCount === 0) return false;
		// 	if (ticketCount === 1) return true;
		// }
		return isExpand;
	}
	return (
		<>
			{(activeFloatBetSlip && isQuickBet) ? null : (
				<section
					ref={selectRef}
					className={clsx(
						'bet-slip',
						betslipStyle.root,
						{
							[betslipStyle.betslipIndependent]: isIndependentMode && isExpand && currentMultiTickets.length > 1 && !activeFloatBetSlip && !isQuickBet,
							[betslipStyle.betslipModal]: activeFloatBetSlip,
							'bet-slip-aside': activeAsideBetSlip,
							[betslipStyle.betslipAside]: activeAsideBetSlip,
							[betslipStyle.empty]: betslipType === 'empty',
							[betslipStyle.hasTicket]: currentMultiTickets.length > 0 || isQuickBet,
						}, {
							[betslipStyle.betslipAsideIndependent]: activeAsideBetSlip && isIndependentMode && currentMultiTickets.length > 1 && !isQuickBet,
						}
					)}
					data-float={!isSingleMatchPage && (betslipType === 'empty' || betslipType === 'single' || isQuickBet || !isExpand) && !activeFloatBetSlip}
					data-type={betslipType}
					data-expand={(isQuickBet ? isQuickbetExpand : getIsBetslipExpand()) || activeAsideBetSlip}
					data-scheduled-open={isBookingBet}
				>
					<BookedBetPromoteHint />
					{activeFloatBetSlip ? <BetslipModalHeader /> : <BasicBetSlipHeaderGroup />}
					<BetslipContainer />
				</section>
			)}
			{betslipType === 'empty' && activeAsideBetSlip ? <AsideEmptyPage /> : null}
			{(activeFloatBetSlip && isExpand) ? <div className="backdrop" onClick={toggleIsExpand}></div> : null}
			{(activeFloatBetSlip && !isParlayMode) ? <FloatQuickBet /> : null}
			<QuickBetSettingModal
				isOpen={isQuickBetSettingModalOpen}
				closeModal={() => setIsQuickBetSettingModalOpen(false)}
			/>
		</>
	);
};
